import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { LogFunction, COLLECTIONS } from './types';
import { isUserAdmin } from './helpers';
import { convertToSerializable } from './helpers';

// Define fields to exclude during export (computed by Cloud Functions or purely denormalized)
const EXCLUDED_FIELDS: Record<string, string[]> = {
  drinks: ['ratingSum', 'averageRating', 'reviewCount', 'sightingCount'],
  locations: ['averageRating', 'ratingCount', 'drinkCount', 'lastSightingDate'],
  sightings: ['confirmationCount', 'lastConfirmed'], // lastConfirmed is updated by trigger
  users: ['usernameLower', 'visitedLocationCount', 'isAdmin'], // isAdmin is checked directly, not exported/imported
  reviews: ['username', 'drinkName', 'locationName'], // Denormalized
  visitedLocations: ['locationName'], // Denormalized
  confirmations: ['username'], // Denormalized
};

// Expected top-level collections based on documentation
// Ensure this matches the definition in types.ts or elsewhere
const COLLECTIONS_TO_EXPORT = COLLECTIONS;

/**
 * Exports all data from Firestore collections to a JSON structure,
 * including specified subcollections and excluding computed fields.
 * @param logFn - Optional logging function
 * @param useNameBasedFormat - (Currently ignored, transformation logic removed in this refactor) Whether to transform the exported data to use name-based references
 */
export const exportFirestoreData = async (logFn?: LogFunction): Promise<string> => {
  const isAdmin = await isUserAdmin();
  if (!isAdmin) {
    logFn?.('Permission denied: Admin access required');
    throw new Error('Permission denied: Admin access required');
  }

  // Use nested structure: { collectionName: { docId: { _docData: {}, _subCollections: { subCollName: { subDocId: {} } } } } }
  const exportData: Record<string, Record<string, {
    _docData: unknown;
    _subCollections?: Record<string, Record<string, unknown>>;
  }>> = {};

  try {
    logFn?.('Starting data export');

    // Fetch data from each top-level collection
    for (const collectionName of COLLECTIONS_TO_EXPORT) {
      logFn?.(`Exporting collection: ${collectionName}`);
      exportData[collectionName] = {};
      const querySnapshot = await getDocs(collection(db, collectionName));

      let docCount = 0;
      for (const doc of querySnapshot.docs) {
        const docData = doc.data();
        const excluded = EXCLUDED_FIELDS[collectionName] || [];
        const filteredData = Object.keys(docData)
          .filter(key => !excluded.includes(key))
          .reduce((obj, key) => {
            obj[key] = docData[key];
            return obj;
          }, {} as Record<string, unknown>);

        exportData[collectionName][doc.id] = {
          _docData: convertToSerializable(filteredData),
          _subCollections: {} // Initialize subcollections container
        };
        docCount++;

        // --- Handle Subcollections ---
        // 1. User Visited Locations
        if (collectionName === 'users') {
          const visitedLocationsRef = collection(db, `users/${doc.id}/visitedLocations`);
          const visitedSnapshot = await getDocs(visitedLocationsRef);
          if (!visitedSnapshot.empty) {
            exportData[collectionName][doc.id]._subCollections!['visitedLocations'] = {};
            visitedSnapshot.forEach(subDoc => {
              const subDocData = subDoc.data();
              const subExcluded = EXCLUDED_FIELDS['visitedLocations'] || [];
              const filteredSubData = Object.keys(subDocData)
                .filter(key => !subExcluded.includes(key))
                .reduce((obj, key) => {
                  obj[key] = subDocData[key];
                  return obj;
                }, {} as Record<string, unknown>);
              exportData[collectionName][doc.id]._subCollections!['visitedLocations'][subDoc.id] = convertToSerializable(filteredSubData);
            });
             logFn?.(`  Exported ${visitedSnapshot.size} documents from subcollection visitedLocations for user ${doc.id}`);
          }
        }

        // 2. Sighting Confirmations
        if (collectionName === 'sightings') {
          const confirmationsRef = collection(db, `sightings/${doc.id}/confirmations`);
          const confirmationSnapshot = await getDocs(confirmationsRef);
          if (!confirmationSnapshot.empty) {
            exportData[collectionName][doc.id]._subCollections!['confirmations'] = {};
            confirmationSnapshot.forEach(subDoc => {
              const subDocData = subDoc.data();
              const subExcluded = EXCLUDED_FIELDS['confirmations'] || [];
              const filteredSubData = Object.keys(subDocData)
                .filter(key => !subExcluded.includes(key))
                .reduce((obj, key) => {
                  obj[key] = subDocData[key];
                  return obj;
                }, {} as Record<string, unknown>);
               exportData[collectionName][doc.id]._subCollections!['confirmations'][subDoc.id] = convertToSerializable(filteredSubData);
            });
             logFn?.(`  Exported ${confirmationSnapshot.size} documents from subcollection confirmations for sighting ${doc.id}`);
          }
        }
        // --- End Subcollection Handling ---
      }
      logFn?.(`Exported ${docCount} documents from ${collectionName}`);
    }

    // Transformation logic removed for this refactor
    // let finalData = exportData;
    // if (useNameBasedFormat) {
    //   logFn?.('Transforming data to name-based reference format (Currently N/A)');
    //   // finalData = transformExportDataToNameBased(exportData); // Transformation logic needs update for new structure
    // }

    // Convert to JSON string
    const jsonString = JSON.stringify(exportData, null, 2);
    logFn?.(`Data export completed successfully (${jsonString.length} characters)`);
    return jsonString;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error exporting Firestore data:', error);
    logFn?.(`Error exporting data: ${errorMessage}`);
    throw new Error(`Failed to export data: ${errorMessage}`);
  }
}; 